import {
  Component,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TripImportGridComponent } from '../trip-import-grid/trip-import-grid.component';
import { DvButtonComponent } from '@devinco/ui/dds';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';
import * as Leaflet from 'leaflet';
import { PolyLinesService } from '@features/map/services/polylines-service';
import {
  RangeSliderChangeEvent,
  RangeSliderComponent,
} from '@shared/components/range-slider/range-slider.component';
import { TripPolylineTrackStoreService } from '@features/map/services/trip-polyline-track-store.service';
import { RestApiService } from '@core/services/rest-api.service';
import { switchMap, tap } from 'rxjs';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import { IRowNode } from 'ag-grid-community';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { EventBusService } from '@shared/services/event-bus/event-bus.service';
import { EventKey } from '@shared/constants/event-bus/event-key';
import { MapInput } from '@features/map/interfaces';
import { DvProgressSpinnerModule } from '@devinco/ui/legacy';
import { SupportUserService } from '@shared/services/support-user.service';
import { TripPoint } from '@pages/trips-page/interfaces';
@Component({
  selector: 'app-trip-import',
  imports: [
    TripImportGridComponent,
    DvButtonComponent,
    TranslateModule,
    RangeSliderComponent,
    DvProgressSpinnerModule,
  ],
  templateUrl: './trip-import.component.html',
  styleUrl: './trip-import.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PolyLinesService, TripPolylineTrackStoreService],
})
export class TripImportComponent {
  private polylinesService = inject(PolyLinesService);
  private polylineTrackService = inject(TripPolylineTrackStoreService);
  private api = inject(RestApiService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);
  private splitterService = inject(VtSplitterService);
  private eventBusService = inject(EventBusService);
  private supportUserService = inject(SupportUserService);

  map = input<Leaflet.Map>();
  overlayDisposeFn = input<() => void>();
  routeNetworkId = input<number>();

  selectedTripsCount = signal<number>(0);
  showRangeSlider = signal<boolean>(false);
  tripPoints = signal<number[]>([]);
  importInProgress = signal<boolean>(false);

  private sliderRangeValue = signal<RangeSliderChangeEvent | undefined>(
    undefined
  );
  private selectedTrackerId = signal<number | undefined>(undefined);

  onTripSelectionChanged(selectedTrips: TripDto[]): void {
    this.selectedTrackerId.set(
      selectedTrips.length ? selectedTrips[0].TrackerID : undefined
    );
    this.selectedTripsCount.set(selectedTrips.length);
    const prevById = new Map(
      this.polylinesService.polyLineLayers.map(layer => [
        layer!.extraData!.polylineSegmentId!,
        layer,
      ])
    );

    const currById = new Map(selectedTrips.map(trip => [trip.TripID!, trip]));

    /** removing polyline layers that don't exist in the new selection */
    for (const [id, layer] of prevById) {
      if (!currById.has(id)) {
        this.map()!.removeLayer(layer);

        const index = this.polylinesService.polyLineLayers.findIndex(
          layer => layer.extraData?.polylineSegmentId === id
        );

        if (index !== -1) {
          this.polylinesService.removePolylineAtIndex(index);
          this.polylineTrackService.removeTrack(layer.extraData!.id!);
        }
      }
    }

    /** adding polyline layers that were newly selected */
    for (const [id, trip] of currById) {
      if (!prevById.has(id)) {
        const segments = this.splitTripPointsByPower(trip.TripPoints || []);

        const addedPolylines = this.polylinesService.drawMapLines(
          segments.map(segment => ({
            isRouteInput: true,
            hideRouteIcons: true,
            routeColor: segment.isExternalPower ? 'blue' : '#A7611D',
            points: segment.points.map(point => ({
              lat: point.Lat,
              lng: point.Lon,
              extraData: {
                id: point.TrackingID,
                polylineSegmentId: trip.TripID,
                editable: false,
              },
            })),
          })) satisfies MapInput[],
          this.map()!,
          true
        );

        addedPolylines.forEach(poly => {
          this.polylineTrackService.addTrack(
            trip.TripID,
            poly as Leaflet.Polyline
          );
        });
      }
    }

    const trackObjectIds = selectedTrips.flatMap(trip =>
      (trip.TripPoints ?? [])
        .filter(point => !!point.TrackingID)
        .map(tp => tp.TrackingID!)
    );
    this.tripPoints.set(trackObjectIds);
  }

  cancel(): void {
    this.destroyTempPolylines();
    this.overlayDisposeFn()?.();
  }

  toggleTripSlider(): void {
    this.showRangeSlider.set(!this.showRangeSlider());
  }

  onSliderRangeChange(event: RangeSliderChangeEvent): void {
    this.sliderRangeValue.set(event);
    const tripPoints = this.tripPoints();
    const startIndex = tripPoints.indexOf(event.startId);
    const endIndex = tripPoints.indexOf(event.endId);
    this.polylineTrackService.updateVisibleBySlider(
      this.tripPoints().slice(startIndex, endIndex + 1)
    );
  }

  backToTripSelection(): void {
    this.polylineTrackService.updateVisibleBySlider(this.tripPoints());
    this.toggleTripSlider();
  }

  save(): void {
    const sliderRangeValue = this.sliderRangeValue();
    this.importInProgress.set(true);
    this.supportUserService
      .isSupportUser()
      .pipe(
        switchMap(isSupportUser => {
          const payload = {
            TrackerID: this.selectedTrackerId(),
            TrackingIDStart: sliderRangeValue?.startId,
            TrackingIDStop: sliderRangeValue?.endId,
            RouteNetworkID: (
              this.splitterService.agGrid.api.getSelectedNodes() as IRowNode<RouteNetwork>[]
            )[0].data?.RouteNetworkID,
          };
          return this.api
            .postCustom('Route/GenerateSkiRouteSegment', payload)
            .pipe(
              tap(() => {
                this.eventBusService.emit({
                  key: EventKey.RouteNetworkRefetchTriggered,
                  data: { doAutoFit: false },
                });
                this.overlayDisposeFn()?.();
                this.snackbar.open(
                  this.translate.instant('TripImport.ImportSuccessful'),
                  SnackBarConfig.Success
                );
                this.polylineTrackService.clearTrackStore();
                this.polylinesService.clearPolylineLayers(this.map()!);
                if (isSupportUser) {
                  const text = `TrackerID: ${payload.TrackerID || '-'}, TrackingIDStart: ${payload.TrackingIDStart}, TrackingIDEnd: ${payload.TrackingIDStop}`;
                  navigator.clipboard.writeText(text);
                }
              })
            );
        })
      )

      .subscribe({
        next: () => this.importInProgress.set(false),
        error: () => this.importInProgress.set(false),
      });
  }

  private destroyTempPolylines(): void {
    this.polylinesService.polyLineLayers.forEach(poly =>
      this.map()?.removeLayer(poly)
    );
  }

  /** splits points array into multiple segments depending on TrackerStatus_ExternalPower value */
  private splitTripPointsByPower(
    points: TripPoint[]
  ): { points: TripPoint[]; isExternalPower: boolean }[] {
    if (!points.length) return [];

    const segments: { points: TripPoint[]; isExternalPower: boolean }[] = [];
    let current: TripPoint[] = [points[0]];
    let currentPower = points[0].TrackerStatus_ExternalPower ?? true;

    for (let i = 1; i < points.length; i++) {
      const point = points[i];
      const power = point.TrackerStatus_ExternalPower ?? true;

      if (power === currentPower) {
        current.push(point);
      } else {
        // To avoid gaps between segments, share the boundary point
        current.push(point);
        segments.push({ points: current, isExternalPower: currentPower });
        current = [point];
        currentPower = power;
      }
    }

    segments.push({ points: current, isExternalPower: currentPower });
    return segments;
  }
}
